import { useState } from 'react';
import { X, RefreshCw, Loader2 } from 'lucide-react';
import { useCancelInvite, useResendInvite } from '../../api/hooks/useTeams';
import { ConfirmModal } from '../modals/ConfirmModal';
import { Badge } from '../feedback/Badge';
import { useToast } from '../../contexts/ToastContext';
import type { TeamInvite } from '../../types';

interface InviteListProps {
  invites: TeamInvite[];
  teamId: number;
}

export function InviteList({ invites, teamId }: InviteListProps) {
  const [cancellingInviteId, setCancellingInviteId] = useState<number | null>(null);
  const cancelInvite = useCancelInvite();
  const resendInvite = useResendInvite();
  const { showToast } = useToast();

  const handleCancelClick = (inviteId: number) => {
    setCancellingInviteId(inviteId);
  };

  const handleCancelConfirm = async () => {
    if (cancellingInviteId) {
      await cancelInvite.mutateAsync({ teamId, inviteId: cancellingInviteId });
      setCancellingInviteId(null);
    }
  };

  const handleResend = async (inviteId: number) => {
    try {
      await resendInvite.mutateAsync({ teamId, inviteId });
      showToast('Invitation resent successfully');
    } catch (error) {
      showToast('Failed to resend invitation', 'error');
    }
  };

  const formatExpiresAt = (dateString: string) => {
    const expiresAt = new Date(dateString);
    const now = new Date();
    const diffMs = expiresAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return 'Expired';
    if (diffDays === 1) return 'Expires tomorrow';
    return `Expires in ${diffDays} days`;
  };

  return (
    <div className="divide-y divide-border">
      {invites.map((invite) => (
        <div
          key={invite.id}
          className="flex items-center justify-between px-4 py-3 hover:bg-background-elevated/50 transition-colors"
        >
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">{invite.email}</span>
              <Badge variant="default" size="sm" pill={false} className="capitalize">
                {invite.role}
              </Badge>
            </div>
            <div className="text-sm text-text-secondary">
              {formatExpiresAt(invite.expires_at)} &middot; Invited by {invite.invited_by.name}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleResend(invite.id)}
              disabled={resendInvite.isPending}
              title="Resend invite"
              className="p-2 rounded-lg hover:bg-background-elevated text-text-secondary hover:text-primary transition-colors disabled:opacity-50"
            >
              {resendInvite.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
            </button>
            <button
              onClick={() => handleCancelClick(invite.id)}
              disabled={cancelInvite.isPending}
              title="Cancel invite"
              className="p-2 rounded-lg hover:bg-error/10 text-text-secondary hover:text-error transition-colors disabled:opacity-50"
            >
              {cancelInvite.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <X size={16} />
              )}
            </button>
          </div>
        </div>
      ))}

      {invites.length === 0 && (
        <div className="px-4 py-8 text-center text-text-secondary">
          No pending invites
        </div>
      )}

      <ConfirmModal
        isOpen={cancellingInviteId !== null}
        title="Cancel Invite"
        message="Are you sure you want to cancel this invitation? The recipient will no longer be able to accept it."
        confirmText="Cancel Invite"
        confirmVariant="danger"
        onConfirm={handleCancelConfirm}
        onClose={() => setCancellingInviteId(null)}
        isLoading={cancelInvite.isPending}
      />
    </div>
  );
}
